/** Ops Console Fleet Vehicles catalog API — `/v2/console/vehicles/`.
 *
 * Cross-tenant roster (tenant is a filter, not a URL segment), mirroring the
 * route_sets / route_stops catalog wrappers. Image/icon uploads use the console
 * multipart pattern (`_json` + files). Hand-authored types (console convention:
 * feature wrappers don't consume the generated `types.ts`).
 */
import { apiDelete, apiGet, apiPatch, apiPost, client } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RouteBoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface VehicleRoute {
  id: number;
  name: string;
  color: string;
  enabled: boolean;
  /** Map bounding box (drawn as a rectangle), or null until stops give it coords. */
  bounding_box: RouteBoundingBox | null;
}

export interface VehicleType {
  id: number;
  name: string;
}

export interface VehicleTag {
  id: number;
  name: string;
}

export interface Vehicle {
  id: number;
  name: string;
  license_plate: string | null;
  ext_id: string | null;
  enabled: boolean;
  status: string | null;
  latitude: number | null;
  longitude: number | null;
  status_ts: string | null;
  /** Owning tour operator ({id,name,code}); the grid's Tenant column. */
  tenant: TenantRef | null;
  route: VehicleRoute | null;
  route_name: string | null;
  route_assignment_ts: string | null;
  route_hints: string[] | null;
  capacity: number;
  type_id: number | null;
  type_name: string | null;
  fleet_mgmt_id: number;
  tags: VehicleTag[];
  icon: { url: string } | null;
  image: { id: number; url: string } | null;
  created_ts: string;
  updated_ts: string;
  last_sync_ts: string | null;
}

export interface VehicleWriteInput {
  name?: string;
  license_plate?: string | null;
  enabled?: boolean;
  capacity?: number;
  type_id?: number | null;
  route_id?: number | null;
  tags?: number[];
  /** Required on create (cross-tenant surface has no URL tenant segment). */
  tour_operator?: number;
  fleet_id?: number;
}

export type BatchAction = 'enable' | 'disable' | 'unassign' | 'delete';

// ---------------------------------------------------------------------------
// List (paginated — feeds the ResourceDataGrid)
// ---------------------------------------------------------------------------

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  fleet_id?: number;
  /** One status, or comma-separated for several. */
  status?: string;
  enabled?: 'true' | 'false';
  route_id?: number;
  assigned?: 'true' | 'false';
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.fleet_id !== undefined) usp.set('fleet_id', String(params.fleet_id));
  if (params.status) usp.set('status', params.status);
  if (params.enabled) usp.set('enabled', params.enabled);
  if (params.route_id !== undefined) usp.set('route_id', String(params.route_id));
  if (params.assigned) usp.set('assigned', params.assigned);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<Vehicle>> {
  return apiGet<PaginatedResponse<Vehicle>>(`/vehicles/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<Vehicle> {
  return apiGet<Vehicle>(`/vehicles/${id}/`);
}

// ---------------------------------------------------------------------------
// Route + type lookups (per tour operator)
// ---------------------------------------------------------------------------

export function listRoutes(toId: number): Promise<{ results: VehicleRoute[] }> {
  return apiGet<{ results: VehicleRoute[] }>(`/vehicle_routes/?tour_operator_id=${toId}`);
}

export function listTypes(toId: number): Promise<{ results: VehicleType[] }> {
  return apiGet<{ results: VehicleType[] }>(`/vehicle_types/?tour_operator_id=${toId}`);
}

// ---------------------------------------------------------------------------
// Create / update (multipart when files are present)
// ---------------------------------------------------------------------------

interface Files {
  image?: File | null;
  icon?: File | null;
}

function hasFiles(files?: Files): boolean {
  return !!(files && (files.image || files.icon));
}

function multipart(data: VehicleWriteInput, files?: Files): FormData {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(data));
  if (files?.image) fd.append('image', files.image);
  if (files?.icon) fd.append('icon', files.icon);
  return fd;
}

export async function create(data: VehicleWriteInput, files?: Files): Promise<Vehicle> {
  if (hasFiles(files)) {
    const r = await client.post<Vehicle>('/vehicles/', multipart(data, files), {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  return apiPost<Vehicle>('/vehicles/', data);
}

export async function update(id: number, data: VehicleWriteInput, files?: Files): Promise<Vehicle> {
  if (hasFiles(files)) {
    const r = await client.patch<Vehicle>(`/vehicles/${id}/`, multipart(data, files), {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  return apiPatch<Vehicle>(`/vehicles/${id}/`, data);
}

export function remove(id: number): Promise<void> {
  return apiDelete<void>(`/vehicles/${id}/`);
}

// ---------------------------------------------------------------------------
// Route assignment (per-row)
// ---------------------------------------------------------------------------

export function assignRoute(id: number, routeId: number): Promise<Vehicle> {
  return apiPost<Vehicle>(`/vehicles/${id}/assign_route/`, { route_id: routeId });
}

export function unassignRoute(id: number): Promise<Vehicle> {
  return apiPost<Vehicle>(`/vehicles/${id}/unassign_route/`);
}
